import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AddressFormData, OverviewStepFormData } from '../types'
import { StorageInstallationStatementModel } from '../models'
import { FormData } from '../types/overview'
import { DEFAULT_PHOTOVOLTAIC_DATA, PhotovoltaicFormData } from 'src/Libs/installation-statement/configurations/photovoltaic'
import { DEFAULT_STORAGE_DATA } from 'src/Libs/installation-statement/configurations/storage'
import { DEFAULT_MEASUREMENTS_DATA } from 'src/Libs/installation-statement/configurations/measurements'
import { ImagesFormData } from 'src/Libs/installation-statement/models/images'
import { getDefaultImageData } from 'src/Libs/installation-statement/utils/images'
import { MeasurementsFormData } from 'src/Libs/installation-statement/models/measurements'
import { EdisonWallBoxFormData } from 'src/Libs/installation-statement/models/edisonWallBox'
import { DEFAULT_EDISON_WALLBOX_DATA } from 'src/Libs/installation-statement/configurations/edisonWallBox'
import { getDefaultAddressData } from 'src/Libs/installation-statement/utils/consts'
import { isEqual } from 'lodash'

interface InstallationStatementStep<T> {
  formData: FormData<T>
  setFormData: (value: FormData<T>) => void
  isFinalValidationValid: boolean
  setFinalValidation: (value: boolean) => void
  isTouched: boolean
  setTouched: (value: boolean) => void
  stepIsPrefilled: boolean
  setStepIsPrefilled: (value: boolean) => void
  label: string
  validationTooltipTitle: string
}

interface UseInstallationStatementHook {
  addressStep: InstallationStatementStep<AddressFormData>
  storageStep: InstallationStatementStep<StorageInstallationStatementModel>
  photovoltaicStep: InstallationStatementStep<PhotovoltaicFormData>
  measurementsStep: InstallationStatementStep<MeasurementsFormData>
  imagesStep: InstallationStatementStep<ImagesFormData>
  overviewStep: InstallationStatementStep<OverviewStepFormData>
  edisonStep: InstallationStatementStep<EdisonWallBoxFormData>
}

/**
 * This custom hook returns the neccessary information for all steps of the installation statement.
 * It also provides the setter functions to update the data and validation used for those steps.
 */
const useInstallationStatement = (deviceLinkId: number, installationId: number, installer?: string): UseInstallationStatementHook => {
  const { t } = useTranslation()

  const [addressFormData, setAddressFormData] = useState<FormData<AddressFormData>>({ values: getDefaultAddressData(installer) })
  const [storageFormData, setStorageFormData] = useState<FormData<StorageInstallationStatementModel>>({
    values: DEFAULT_STORAGE_DATA,
  })
  const [photovoltaicFormData, setPhotovoltaicFormData] = useState<FormData<PhotovoltaicFormData>>({ values: DEFAULT_PHOTOVOLTAIC_DATA })
  const [measurementsFormData, setMeasurementsFormData] = useState<FormData<MeasurementsFormData>>({ values: DEFAULT_MEASUREMENTS_DATA })
  const [overViewFormData, setOverViewFormData] = useState<FormData<OverviewStepFormData>>({ values: { accepted: false } })
  const [imagesFormData, setImagesFormData] = useState<FormData<ImagesFormData>>({ values: getDefaultImageData(deviceLinkId, installationId) })
  const [edisonWallBoxFormData, setEdisonWallBoxFormData] = useState<FormData<EdisonWallBoxFormData>>({ values: DEFAULT_EDISON_WALLBOX_DATA })

  /**
   * This is necessary to check if the data has changed and update the state accordingly. This fixes the delay that slowed down the pv step massively.
   */
  const updatePhotovoltaicFormData = useCallback(
    (data: FormData<PhotovoltaicFormData>): void => {
      if (!isEqual(data.values, photovoltaicFormData.values)) {
        setPhotovoltaicFormData(data)
      }
    },
    [photovoltaicFormData]
  )

  const [addressIsFinalValidationValid, setAddressIsFinalValidationValid] = useState(false)
  const [storageIsFinalValidationValid, setStorageIsFinalValidationValid] = useState(false)
  const [photovoltaicIsFinalValidationValid, setPhotovoltaicIsFinalValidationValid] = useState(false)
  const [imagesIsFinalValidationValid, setImagesIsFinalValidationValid] = useState(false)
  const [measurementsIsFinalValidationValid, setMeasurementsIsFinalValidationValid] = useState(false)
  const [edisonWallBoxIsFinalValidationValid, setEdisonWallBoxIsFinalValidationValid] = useState(false)

  const [addressTouched, setAddressTouched] = useState(false)
  const [storageTouched, setStorageTouched] = useState(false)
  const [photoVoltaicTouched, setPhotoVoltaicTouched] = useState(false)
  const [overViewTouched, setOverViewTouched] = useState(false)
  const [imagesTouched, setImagesTouched] = useState(false)
  const [measurementsTouched, setMeasurementsTouched] = useState(false)
  const [edisonWallBoxTouched, setEdisonWallBoxTouched] = useState(false)

  const [isAddressPrefilled, setIsAddressPrefilled] = useState(false)
  const [isStoragePrefilled, setIsStoragePrefilled] = useState(false)
  const [isPhotoVoltaicPrefilled, setIsPhotoVoltaicPrefilled] = useState(false)
  const [isOverviewPrefilled, setIsOverviewPrefilled] = useState(false)
  const [isImagePrefilled, setIsImagePrefilled] = useState(false)
  const [isMeasurementsPrefilled, setIsMeasurementsPrefilled] = useState(false)
  const [isEdisonWallBoxPrefilled, setIsEdisonWallBoxPrefilled] = useState(false)

  const addressStep: InstallationStatementStep<AddressFormData> = useMemo(
    () => ({
      formData: addressFormData,
      setFormData: setAddressFormData,
      isFinalValidationValid: addressIsFinalValidationValid,
      setFinalValidation: setAddressIsFinalValidationValid,
      isTouched: addressTouched,
      setTouched: setAddressTouched,
      stepIsPrefilled: isAddressPrefilled,
      setStepIsPrefilled: setIsAddressPrefilled,
      label: t('installation-statement.address.title'),
      validationTooltipTitle: t(''),
    }),
    [addressFormData, addressIsFinalValidationValid, addressTouched, isAddressPrefilled, t]
  )

  const storageStep: InstallationStatementStep<StorageInstallationStatementModel> = useMemo(
    () => ({
      formData: storageFormData,
      setFormData: setStorageFormData,
      isFinalValidationValid: storageIsFinalValidationValid,
      setFinalValidation: setStorageIsFinalValidationValid,
      isTouched: storageTouched,
      setTouched: setStorageTouched,
      stepIsPrefilled: isStoragePrefilled,
      setStepIsPrefilled: setIsStoragePrefilled,
      label: t('installation-statement.storage.title'),
      validationTooltipTitle: t(''),
    }),
    [isStoragePrefilled, storageFormData, storageIsFinalValidationValid, storageTouched, t]
  )

  const photovoltaicStep: InstallationStatementStep<PhotovoltaicFormData> = useMemo(
    () => ({
      formData: photovoltaicFormData,
      setFormData: updatePhotovoltaicFormData,
      isFinalValidationValid: photovoltaicIsFinalValidationValid,
      setFinalValidation: setPhotovoltaicIsFinalValidationValid,
      isTouched: photoVoltaicTouched,
      setTouched: setPhotoVoltaicTouched,
      stepIsPrefilled: isPhotoVoltaicPrefilled,
      setStepIsPrefilled: setIsPhotoVoltaicPrefilled,
      label: t('installation-statement.photovoltaic.title'),
      validationTooltipTitle: t(''),
    }),
    [photovoltaicFormData, updatePhotovoltaicFormData, photovoltaicIsFinalValidationValid, photoVoltaicTouched, isPhotoVoltaicPrefilled, t]
  )

  const measurementsStep: InstallationStatementStep<MeasurementsFormData> = useMemo(
    () => ({
      formData: measurementsFormData,
      setFormData: setMeasurementsFormData,
      isFinalValidationValid: measurementsIsFinalValidationValid,
      setFinalValidation: setMeasurementsIsFinalValidationValid,
      isTouched: measurementsTouched,
      setTouched: setMeasurementsTouched,
      stepIsPrefilled: isMeasurementsPrefilled,
      setStepIsPrefilled: setIsMeasurementsPrefilled,
      label: t('installation-statement.measurement.title'),
      validationTooltipTitle: t(''),
    }),
    [isMeasurementsPrefilled, measurementsFormData, measurementsIsFinalValidationValid, measurementsTouched, t]
  )

  const imagesStep: InstallationStatementStep<ImagesFormData> = useMemo(
    () => ({
      formData: imagesFormData,
      setFormData: setImagesFormData,
      isFinalValidationValid: imagesIsFinalValidationValid,
      setFinalValidation: setImagesIsFinalValidationValid,
      isTouched: imagesTouched,
      setTouched: setImagesTouched,
      stepIsPrefilled: isImagePrefilled,
      setStepIsPrefilled: setIsImagePrefilled,
      label: t('installation-statement.images.label'),
      validationTooltipTitle: t('installation-statement.images.validation-tooltip-title'),
    }),
    [isImagePrefilled, imagesFormData, imagesIsFinalValidationValid, imagesTouched, t]
  )

  const edisonStep: InstallationStatementStep<EdisonWallBoxFormData> = useMemo(
    () => ({
      formData: edisonWallBoxFormData,
      setFormData: setEdisonWallBoxFormData,
      isFinalValidationValid: edisonWallBoxIsFinalValidationValid,
      setFinalValidation: setEdisonWallBoxIsFinalValidationValid,
      isTouched: edisonWallBoxTouched,
      setTouched: setEdisonWallBoxTouched,
      stepIsPrefilled: isEdisonWallBoxPrefilled,
      setStepIsPrefilled: setIsEdisonWallBoxPrefilled,
      label: t('installation-statement.edison.label'),
      validationTooltipTitle: t('installation-statement.edison.validation-tooltip-title'),
    }),
    [edisonWallBoxFormData, edisonWallBoxIsFinalValidationValid, edisonWallBoxTouched, isEdisonWallBoxPrefilled, t]
  )

  const overviewStep: InstallationStatementStep<OverviewStepFormData> = useMemo(
    () => ({
      formData: overViewFormData,
      setFormData: setOverViewFormData,
      isFinalValidationValid: true,
      setFinalValidation: () => ({}),
      isTouched: overViewTouched,
      setTouched: setOverViewTouched,
      stepIsPrefilled: isOverviewPrefilled,
      setStepIsPrefilled: setIsOverviewPrefilled,
      label: t('installation-statement.overview.title'),
      validationTooltipTitle: t(''),
    }),
    [isOverviewPrefilled, overViewFormData, overViewTouched, t]
  )

  return { addressStep, storageStep, photovoltaicStep, overviewStep, imagesStep, measurementsStep, edisonStep }
}

export default useInstallationStatement
